/**
 * @typedef {Object} OpeningStatistics
 * @property {boolean} has_data
 * @property {string|null} last_opened_on
 * @property {number|null} avg_interval_seconds
 * @property {string|null} avg_interval_display
 * @property {number} interval_sample_count
 * @property {number|null} estimated_opens_per_month
 * @property {number|null} estimated_opens_per_year
 */

/** @type {OpeningStatistics} */
export const EMPTY_OPENING_STATISTICS = {
  has_data: false,
  last_opened_on: null,
  avg_interval_seconds: null,
  avg_interval_display: null,
  interval_sample_count: 0,
  estimated_opens_per_month: null,
  estimated_opens_per_year: null,
};

/**
 * Converts yyyy-mm-dd to dd/mm/yyyy (matches helpers.formatDateStr without axios deps).
 *
 * @param {string} dateStr
 * @returns {string}
 */
function formatDatePart(dateStr) {
  if (!dateStr) return '';
  const [year, month, day] = dateStr.split('-');
  return `${day}/${month}/${year}`;
}

/**
 * @param {number|string|null|undefined} num
 * @returns {number|string}
 */
function formatDecimalNumberLocal(num) {
  if (num === null || num === undefined || num === '') {
    return '';
  }

  const numStr = num.toString();
  if (numStr.includes('.')) {
    const [integerPart, decimalPart] = numStr.split('.');
    if (decimalPart.length === 1 && decimalPart[0] === '0') {
      return parseInt(integerPart, 10);
    }
    if (
      decimalPart.length > 1 &&
      decimalPart[1] === '0' &&
      decimalPart[0] === '0'
    ) {
      return parseInt(integerPart, 10);
    }
    return parseFloat(numStr).toFixed(2);
  }

  return num;
}

/**
 * Parse `opening_statistics` from a product detail API response.
 *
 * @param {Object|null|undefined} product
 * @returns {OpeningStatistics}
 */
export function getOpeningStatistics(product) {
  const stats = product?.opening_statistics;
  if (!stats || typeof stats !== 'object') {
    return { ...EMPTY_OPENING_STATISTICS };
  }
  return {
    has_data: Boolean(stats.has_data),
    last_opened_on: stats.last_opened_on ?? null,
    avg_interval_seconds:
      stats.avg_interval_seconds != null ? stats.avg_interval_seconds : null,
    avg_interval_display: stats.avg_interval_display ?? null,
    interval_sample_count:
      typeof stats.interval_sample_count === 'number'
        ? stats.interval_sample_count
        : 0,
    estimated_opens_per_month:
      stats.estimated_opens_per_month != null
        ? stats.estimated_opens_per_month
        : null,
    estimated_opens_per_year:
      stats.estimated_opens_per_year != null
        ? stats.estimated_opens_per_year
        : null,
  };
}

/**
 * True when enough interval samples exist to show frequency estimates.
 *
 * @param {OpeningStatistics|null|undefined} stats
 * @returns {boolean}
 */
export function hasOpeningInterval(stats) {
  return (
    (stats?.interval_sample_count ?? 0) >= 1 &&
    stats?.avg_interval_seconds != null
  );
}

/**
 * Format an ISO-8601 timestamp for locale display (dd/mm/yyyy).
 *
 * @param {string|null|undefined} isoTimestamp
 * @returns {string|null}
 */
export function formatOpeningDate(isoTimestamp) {
  if (!isoTimestamp) return null;
  const datePart = String(isoTimestamp).split('T')[0];
  if (!datePart || datePart.split('-').length !== 3) return null;
  return formatDatePart(datePart);
}

/**
 * Format an estimated opens count with a tilde prefix.
 *
 * @param {number|null|undefined} value
 * @returns {string|null}
 */
export function formatEstimatedOpens(value) {
  if (value == null || value === '') return null;
  const formatted = formatDecimalNumberLocal(value);
  if (formatted === '' || formatted == null) return null;
  return `~${formatted}`;
}

/**
 * Compact summary copy for inventory modals.
 *
 * @param {OpeningStatistics|null|undefined} stats
 * @returns {string}
 */
export function getOpeningStatisticsSummary(stats) {
  const normalized = stats ?? EMPTY_OPENING_STATISTICS;

  if (hasOpeningInterval(normalized) && normalized.avg_interval_display) {
    const perMonth = formatEstimatedOpens(normalized.estimated_opens_per_month);
    const monthSuffix = perMonth ? ` (${perMonth}/mo)` : '';
    return `Opens ~every ${normalized.avg_interval_display}${monthSuffix}`;
  }

  const lastOpened = formatOpeningDate(normalized.last_opened_on);
  if (lastOpened) {
    return `Last opened ${lastOpened}`;
  }

  return 'No opening history yet';
}

/**
 * Labeled rows for the inventory modal detail layout.
 * Omits rows whose values would be null/empty.
 *
 * @param {OpeningStatistics|null|undefined} stats
 * @returns {Array<{ label: string, value: string }>}
 */
export function getOpeningStatisticsRows(stats) {
  const normalized = stats ?? EMPTY_OPENING_STATISTICS;
  const rows = [];

  const lastOpened = formatOpeningDate(normalized.last_opened_on);
  if (lastOpened) {
    rows.push({ label: 'Last opened', value: lastOpened });
  }

  if (hasOpeningInterval(normalized) && normalized.avg_interval_display) {
    rows.push({
      label: 'Typical interval',
      value: `every ~${normalized.avg_interval_display}`,
    });
  }

  const perMonth = formatEstimatedOpens(normalized.estimated_opens_per_month);
  if (perMonth) {
    rows.push({ label: 'Est. opens / month', value: perMonth });
  }

  const perYear = formatEstimatedOpens(normalized.estimated_opens_per_year);
  if (perYear) {
    rows.push({ label: 'Est. opens / year', value: perYear });
  }

  return rows;
}

/**
 * True when the product has any opening history to show beyond the empty state.
 *
 * @param {OpeningStatistics|null|undefined} stats
 * @returns {boolean}
 */
export function hasOpeningHistory(stats) {
  const normalized = stats ?? EMPTY_OPENING_STATISTICS;
  return Boolean(normalized.has_data || normalized.last_opened_on);
}

/**
 * True when a stock item save may affect product-level opening statistics.
 *
 * @param {Object} params
 * @param {{ openedOn: string, inUse: boolean }|null} [params.snapshot] - Values when edit began
 * @param {string|null|undefined} params.openedOn - Submitted opened-on value
 * @param {boolean} params.inUse - Submitted in-use flag
 * @param {boolean} [params.isCreate=false] - Whether this is a new stock item
 * @returns {boolean}
 */
export function stockItemOpeningHistoryMayHaveChanged({
  snapshot,
  openedOn,
  inUse,
  isCreate = false,
}) {
  if (isCreate) {
    return Boolean(openedOn) || inUse === true;
  }
  if (!snapshot) return false;
  return (
    String(snapshot.openedOn || '') !== String(openedOn || '') ||
    Boolean(snapshot.inUse) !== Boolean(inUse)
  );
}

/**
 * Describe a stock item save in terms of opening history.
 *
 * `closed` means the lot went from in use to not in use, which is what a user
 * does when they un-mark a lot as opened. Callers need that direction because a
 * close removes the opening record instead of adding one, and for a kit lot the
 * backend also closes the kit's component lots.
 *
 * @param {Object} params
 * @param {{ openedOn: string, inUse: boolean }|null} [params.snapshot] - Values when edit began
 * @param {string|null|undefined} params.openedOn - Submitted opened-on value
 * @param {boolean} params.inUse - Submitted in-use flag
 * @param {boolean} [params.isCreate=false] - Whether this is a new stock item
 * @returns {{ changed: boolean, closed: boolean }}
 */
export function resolveStockItemOpeningChange({
  snapshot,
  openedOn,
  inUse,
  isCreate = false,
}) {
  const changed = stockItemOpeningHistoryMayHaveChanged({
    snapshot,
    openedOn,
    inUse,
    isCreate,
  });

  // A brand new lot has nothing to close, and without a snapshot the previous
  // in-use state is unknown.
  const closed =
    changed &&
    !isCreate &&
    Boolean(snapshot?.inUse) &&
    Boolean(inUse) === false;

  return { changed, closed };
}

/**
 * True when any bulk-created stock item includes opening data.
 *
 * @param {Array<{ opened_on?: string, in_use?: boolean }>} items
 * @returns {boolean}
 */
export function bulkStockItemsAffectOpeningHistory(items) {
  if (!Array.isArray(items)) return false;
  return items.some(item => Boolean(item.opened_on) || item.in_use === true);
}
